/**
 * Onboarding interview — pure, tree-driven helpers shared by the chat
 * renderer, the connect step, and the agent roster. No React here; every
 * function is a straight input->output mapping so it can be unit tested
 * without mounting anything (ONBOARDING-FLOW-SPEC.md).
 *
 * The tree itself is the only source of step semantics — these helpers
 * walk it generically (by id, by next/next_by_answer) rather than
 * hardcoding which steps exist, mirroring the backend's own
 * next_unanswered/resolve_next (marketing_suite/interview.py). Two small
 * exceptions are noted below: GOAL_HERO_METRIC and the pre-check rule
 * mirror backend-only pure functions that aren't exposed over the API.
 */

import type { BudgetEntry, InterviewTree, TreeStep } from '../../services/onboardingApi';
import type { Specialist } from '../../types/marketingSuite';

export function stepById(tree: InterviewTree, id: string): TreeStep | undefined {
  return tree.steps.find((step) => step.id === id);
}

/** The next step id for an answered step — branching is data, exactly
 * like the backend's resolve_next. */
export function resolveNext(
  tree: InterviewTree,
  step: TreeStep,
  answer: unknown
): string {
  if (
    typeof answer === 'string' &&
    Object.prototype.hasOwnProperty.call(step.next_by_answer, answer)
  ) {
    return step.next_by_answer[answer];
  }
  return step.next ?? tree.terminal_step;
}

export interface WalkEntry {
  step: TreeStep;
  answer: unknown;
}

/**
 * Replays the branch from the first step using only the answers map
 * (which, unlike an array, carries no order of its own) — the ordered
 * list of answered steps, i.e. the chat history. Stops at the first step
 * without an answer, which is where session.step_key should point.
 */
export function walkPath(
  tree: InterviewTree,
  answers: Record<string, unknown>
): WalkEntry[] {
  const path: WalkEntry[] = [];
  let currentId = tree.first_step;
  const seen = new Set<string>();
  while (currentId !== tree.terminal_step && !seen.has(currentId)) {
    seen.add(currentId);
    const step = stepById(tree, currentId);
    if (!step || !(step.id in answers)) break;
    const answer = answers[step.id];
    path.push({ step, answer });
    currentId = resolveNext(tree, step, answer);
  }
  return path;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(',')}}`;
}

/** 32-bit FNV-1a. Not cryptographic — just a compact, deterministic
 * digest so a bounded (<=255 char) transition_id can still be
 * content-addressed on (session, step, answer). A collision merely falls
 * back to the server's own transition_conflict 422, which asks for a
 * fresh id: an acceptable risk for a client-chosen idempotency key. */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Deterministic transition_id: the SAME (session, step, answer) always
 * produces the SAME id, so a retry, a double-click, or a resumed session
 * replaying its last answer lands on the server's idempotent-replay path
 * instead of minting a duplicate transition (spec §4 build-map item 1). */
export function computeTransitionId(
  sessionId: string,
  stepId: string,
  answer: unknown
): string {
  const digest = fnv1a(`${sessionId}:${stepId}:${stableStringify(answer)}`);
  return `${stepId}:${digest}`;
}

/** A skipped source leaves its dependent agents asleep by design (spec
 * §0) — eligibility is exactly "every required connector is connected". */
export function isEligible(
  specialist: Pick<Specialist, 'required_connectors'>,
  connectedKeys: ReadonlySet<string>
): boolean {
  return specialist.required_connectors.every((key) => connectedKeys.has(key));
}

/**
 * Mirrors the PRE-CHECK half of marketing_suite/interview.py's
 * autonomy_defaults(): agents that recommend no actions are safe to
 * pre-check; a "cautious" posture narrows that further to the read-only
 * analytics pack. Not exposed over the API, so replicated here from
 * fields the specialists endpoint already returns (category,
 * recommended_action_types). The function's OTHER output —
 * suggest_tier1_auto — has no UI surface in this build and is not
 * mirrored; posture never widens which actions still need approval
 * either way (the engine's Tier-1 floor, not a frontend concern).
 */
export function isPrechecked(
  specialist: Pick<Specialist, 'category' | 'recommended_action_types'>,
  posture: string | null | undefined
): boolean {
  if (specialist.recommended_action_types.length > 0) return false;
  if (posture === 'cautious') return specialist.category === 'analytics';
  return true;
}

/** Mirrors marketing_suite/interview.py::GOAL_HERO_METRIC. Not exposed
 * over the API (only the goal step's chip options are, and those are
 * generic strings with no metric mapping attached), so replicated here.
 * "meetings" has no meetings metric — sqls is the closest honest funnel
 * stage, per the backend's own comment; the hero card always renders the
 * METRIC's own label, never the raw goal word. */
export const GOAL_HERO_METRIC: Record<string, string> = {
  revenue: 'revenue_net',
  roas: 'net_roas',
  pipeline: 'opps',
  meetings: 'sqls',
};

/** Cosmetic-only prettifier ("b2b_saas" -> "B2b Saas"). Deliberately
 * generic (string-shape, not a per-value lookup table) so a tree version
 * that adds a new chip option renders sensibly with no frontend change —
 * the submitted answer is always the raw canonical value regardless. */
export function prettifyOption(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function describeBudgetEntry(entry: BudgetEntry): string {
  // Locale pinned (not the environment default) so this render is
  // deterministic regardless of the host's locale — the same reasoning
  // as the semantic-layer currency formatter, applied to plain amounts.
  const amount = `${entry.currency} ${entry.amount.toLocaleString('en-US')}/mo`;
  return entry.scope === 'blended'
    ? `${amount} blended`
    : `${amount} on ${prettifyOption(entry.platform ?? '')}`;
}

/** Human-readable summary of an answered step, for the chat history. */
export function describeAnswer(step: TreeStep, answer: unknown): string {
  switch (step.kind) {
    case 'chips':
      // The tree's own label wins where it has one (spec #47 D6), so the
      // history reads back exactly as the chip the human clicked.
      return typeof answer === 'string'
        ? step.option_labels?.[answer] ?? prettifyOption(answer)
        : String(answer);
    case 'number':
      return typeof answer === 'number' ? answer.toLocaleString('en-US') : String(answer);
    case 'list':
      return Array.isArray(answer) && answer.length
        ? (answer as string[]).join(', ')
        : 'Skipped';
    case 'budget':
      return Array.isArray(answer) && answer.length
        ? (answer as BudgetEntry[]).map(describeBudgetEntry).join('; ')
        : 'Skipped';
    default:
      return String(answer ?? '');
  }
}
